import { useEffect, useRef, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GOOGLE_REVIEW_AUTHOR_MAX,
  GOOGLE_REVIEW_TEXT_MAX,
  type GoogleReviewAdminItem,
} from "@ckgo2/shared";
import { googleManualReviewSchema, todayUtc } from "@ckgo2/shared/schemas";
import { useEditPage } from "@/hooks/useEditPage";
import EditPageHeader from "@/components/common/EditPageHeader";
import {
  GOOGLE_REVIEWS_QUERY_KEY,
  suggestGoogleReviewDestination,
} from "@/components/googleReviews/googleReviewsApi";
import {
  useDestinationOptions,
  withCurrent,
} from "@/components/googleReviews/useDestinationOptions";

/**
 * Formulář má PRÁVĚ klíče `googleManualReviewSchema` (strict): cizí klíč by resolver
 * odmítl. Hvězdy se nezadávají (ruční Google recenze má vždy pět).
 */
export interface GoogleManualForm {
  authorName: string;
  /** `YYYY-MM-DD` */
  reviewDate: string;
  content: string;
  destinationId: string | null;
}

/** Odklad návrhu destinace po psaní a nejkratší text, ke kterému se návrh ptá. */
const SUGGESTION_DELAY_MS = 600;
const SUGGESTION_MIN_CHARS = 3;

/**
 * Ruční Google recenze (F2.6): autor, datum, text a destinace předvyplněná návrhem
 * z textu (F2.7). Klíč `destinationId` se posílá JEN, když ho redakce v tomto formuláři
 * sama zvolila nebo vyprázdnila (pak server destinaci zamkne); nedotčená destinace, i
 * předvyplněná návrhem, klíč vynechá a server ji přiřadí sám podle konečného textu
 * (u zamčeného řádku drží svou). Exportovanou recenzi upravit nejde.
 */
export default function GoogleReviewEditPage() {
  const { t } = useTranslation();
  // Čte se v uzávěru `transformOnSave` až při odeslání, proto funguje i v objektu voleb.
  const userPickedDestinationRef = useRef(false);
  const suggestionSeqRef = useRef(0);

  const { isNew, isLoading, isError, form, saveMutation, onSubmit, itemData } = useEditPage<
    GoogleManualForm,
    GoogleReviewAdminItem
  >({
    resourceKey: GOOGLE_REVIEWS_QUERY_KEY,
    listKey: GOOGLE_REVIEWS_QUERY_KEY,
    apiPath: "/api/admin/google-reviews",
    schema: googleManualReviewSchema,
    navigateTo: "/google-reviews",
    defaultValues: { authorName: "", reviewDate: "", content: "", destinationId: null },
    transformOnLoad: (item) => ({
      authorName: item.authorName,
      reviewDate: item.reviewDate.slice(0, 10),
      content: item.content ?? "",
      destinationId: item.destinationId,
    }),
    transformOnSave: (data) => ({
      authorName: data.authorName.trim(),
      reviewDate: data.reviewDate,
      content: data.content,
      ...(userPickedDestinationRef.current ? { destinationId: data.destinationId } : {}),
    }),
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = form;

  const destinationOptions = withCurrent(useDestinationOptions(), itemData);
  const content = watch("content");

  // Návrh destinace jen u nové recenze a jen dokud redakce destinaci nezvolila sama.
  // Odložený časovač s pořadovým číslem: pozdní odpověď k staršímu textu se zahodí
  // a dvojí běh efektu ve StrictMode nechá jen jeden časovač. Chyba (readonly 403,
  // síť) se tiše ignoruje, formulář funguje dál.
  useEffect(() => {
    const seq = ++suggestionSeqRef.current;
    if (!isNew || userPickedDestinationRef.current) return;
    const text = (content ?? "").trim();
    if (text.length < SUGGESTION_MIN_CHARS) return;
    const timer = setTimeout(() => {
      suggestGoogleReviewDestination(text)
        .then((destinationId) => {
          if (seq !== suggestionSeqRef.current || userPickedDestinationRef.current) return;
          setValue("destinationId", destinationId ?? null, { shouldDirty: false });
        })
        .catch(() => undefined);
    }, SUGGESTION_DELAY_MS);
    return () => clearTimeout(timer);
  }, [content, isNew, setValue]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card text-error-600 space-y-3 text-center">
        <p>{t("content.googleReviews.form.loadError")}</p>
        <Link to="/google-reviews" className="btn-secondary">
          {t("content.googleReviews.form.backToList")}
        </Link>
      </div>
    );
  }

  if (itemData?.origin === "export") {
    return (
      <div className="card space-y-3 text-center text-gray-700 dark:text-gray-300">
        <p>{t("content.googleReviews.form.exportNotEditable")}</p>
        <Link to="/google-reviews" className="btn-secondary">
          {t("content.googleReviews.form.backToList")}
        </Link>
      </div>
    );
  }

  const destinationField = register("destinationId", {
    setValueAs: (v: unknown) => (typeof v === "string" && v !== "" ? v : null),
  });
  const onDestinationChange = (event: ChangeEvent<HTMLSelectElement>) => {
    userPickedDestinationRef.current = true;
    return destinationField.onChange(event);
  };

  return (
    <div className="space-y-6">
      <EditPageHeader
        backUrl="/google-reviews"
        isNew={isNew}
        newTitle={t("content.googleReviews.form.newTitle")}
        editTitle={t("content.googleReviews.form.editTitle")}
        entityName={itemData?.authorName}
        formId="google-review-form"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        error={saveMutation.error}
        validationErrors={errors}
        isSuccess={saveMutation.isSuccess}
      />

      <form
        id="google-review-form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className="card max-w-3xl space-y-4"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="google-review-author" className="label">
              {t("content.googleReviews.form.author")}
            </label>
            <input
              id="google-review-author"
              {...register("authorName")}
              maxLength={GOOGLE_REVIEW_AUTHOR_MAX}
              className="input"
            />
            {errors.authorName && (
              <p className="mt-1 text-sm text-red-500">{errors.authorName.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="google-review-date" className="label">
              {t("content.googleReviews.form.date")}
            </label>
            <input
              id="google-review-date"
              type="date"
              max={todayUtc()}
              {...register("reviewDate")}
              className="input"
            />
            {errors.reviewDate && (
              <p className="mt-1 text-sm text-red-500">{errors.reviewDate.message}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="google-review-content" className="label">
            {t("content.googleReviews.form.text")}
          </label>
          <textarea
            id="google-review-content"
            rows={6}
            {...register("content")}
            maxLength={GOOGLE_REVIEW_TEXT_MAX}
            className="input"
          />
          <p className="mt-1 text-sm text-gray-500">{t("content.googleReviews.form.textHint")}</p>
          {errors.content && <p className="mt-1 text-sm text-red-500">{errors.content.message}</p>}
        </div>

        <div>
          <label htmlFor="google-review-destination" className="label">
            {t("content.googleReviews.form.destination")}
          </label>
          <select
            id="google-review-destination"
            {...destinationField}
            onChange={onDestinationChange}
            className="input"
          >
            <option value="">{t("content.googleReviews.form.destinationNone")}</option>
            {destinationOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-sm text-gray-500">
            {t("content.googleReviews.form.destinationHint")}
          </p>
          {errors.destinationId && (
            <p className="mt-1 text-sm text-red-500">{errors.destinationId.message}</p>
          )}
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("content.googleReviews.form.starsHint")}
        </p>
      </form>
    </div>
  );
}
